import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Button,
  DialogModal,
  Drawer,
  PanelHeader,
  Tabs,
  Text,
  TextAreaController,
  TextController,
} from "@pushpress/shared-components";
import { defaultToastOptions, extractError } from "../../utils/misc";
import { LocationForLocationsPageFragment } from "../../graphql/graphql-generated.types";
import {
  useCreateLocationMutation,
  useUpdateLocationMutation,
} from "../../apis/locationsApi.hooks";
import { validateUrl } from "../../utils/validateUtils";

export interface LocationForm {
  name: string;
  linkName?: string | null;
  linkUrl?: string | null;
}

interface LocationDrawerProps {
  location?: LocationForLocationsPageFragment;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function LocationDrawer(props: LocationDrawerProps) {
  const { visible } = props;

  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="420px"
      disableClickAway
      disableEscapeKeyDown
    >
      <LocationDrawerContent {...props} />
    </Drawer>
  );
}

function LocationDrawerContent(props: LocationDrawerProps) {
  const { location, onClose, onSuccess } = props;

  const [saving, setSaving] = useState(false);

  const [activeTab, setActiveTab] = useState(() =>
    location?.isVirtual ? 1 : 0,
  );

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const updateLocationMutation = useUpdateLocationMutation();

  const createLocationMutation = useCreateLocationMutation();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<LocationForm>({
    mode: "onTouched",
    defaultValues: location,
  });

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      onClose();
    }
  };

  const save = async (values: LocationForm) => {
    try {
      setSaving(true);
      if (location) {
        await updateLocationMutation.mutateAsync({
          uuid: location.uuid,
          name: values.name,
          linkName: values.linkName!,
          linkUrl: values.linkUrl!,
          isVirtual: activeTab === 1,
        });
      } else {
        await createLocationMutation.mutateAsync({
          name: values.name,
          linkName: values.linkName!,
          linkUrl: values.linkUrl!,
          isVirtual: activeTab === 1,
        });
      }
      toast.success("Save successful", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    handleSubmit(save)();
  };

  const physicalTabSelected = activeTab === 0;
  const virtualTabSelected = activeTab === 1;

  return (
    <>
      <PanelHeader title="Location">
        <PanelHeader.ActionMenu>
          <Button
            text="Close"
            buttonType="secondary"
            size="medium"
            onClick={handleClose}
          />
          <Button
            text="Save"
            buttonType="primary"
            size="medium"
            type="button"
            loading={saving}
            onClick={handleSave}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <div style={{ padding: 24 }}>
        <form onSubmit={handleSubmit(save)}>
          <Text variant="body-lg" color="wolf-800">
            Locations can be added to classes or events. They will show up in
            the member app, and on landing pages.{" "}
          </Text>
          <Tabs
            tabHeaders={["Physical", "Virtual"]}
            variant="buttons"
            selectedIndex={activeTab}
            changeTab={setActiveTab}
            className="mt-4 mb-4"
          />
          {physicalTabSelected && (
            <>
              <TextController
                control={control}
                name="name"
                label="Name"
                required
              />
              <TextAreaController
                control={control}
                name="linkName"
                label="Location"
                helperText="E.g. address"
                required
                className="mb-4"
              />
              <TextController
                control={control}
                name="linkUrl"
                label="Location URL"
                required
                validate={validateUrl}
              />
            </>
          )}
          {virtualTabSelected && (
            <>
              <TextController
                control={control}
                name="name"
                label="Name"
                required
              />
              <TextController
                control={control}
                name="linkName"
                label="Link name"
                required
                helperText="E.g. “Click here to join class”"
                className="mb-4"
              />
              <TextController
                control={control}
                name="linkUrl"
                label="Link URL"
                required
                helperText="E.g. Zoom link"
                validate={validateUrl}
              />
            </>
          )}
        </form>
      </div>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
    </>
  );
}

export default LocationDrawer;
